import exifr from 'exifr';
import { PATH_ANALYSIS_CONFIG } from './imageAnalysisConfig.js';
import {
  calculateDistance,
  calculateSlope,
  calculateVectorAngle,
  calculateDistanceSummary,
  calculateStandardDeviation,
  generatePointsByDivision,
  convertToDegrees,
  convertDatetimeFormat
} from './geometryUtils.js';
import { PathAnalyzer } from './pathAnalysis.js';

/**
 * 드론 카메라 pitch 값에 따른 지표면 위치 보정.
 * pitch가 Nadir(-90°)가 아니면 offset = altitude × tan(|pitch + 90|) 만큼 보정.
 * @param {number} lat - 원본 위도
 * @param {number} lon - 원본 경도
 * @param {number} altitude - GPS 상대 고도 (m)
 * @param {number} pitch - 짐벌 pitch 각도 (-90 ~ 90)
 * @param {number} [yaw] - 드론 heading 각도 (0 ~ 360)
 * @returns {{ lat: number, lon: number }} 보정된 좌표
 */
function correctGroundPosition(lat, lon, altitude, pitch, yaw) {
  // pitch가 Nadir이거나 고도가 0 이하면 보정 불필요
  if (pitch == null || altitude == null || altitude <= 0) return { lat, lon };
  if (pitch === -90) return { lat, lon };

  const pitchOffsetDeg = Math.abs(pitch + 90); // Nadir에서 벗어난 각도
  if (pitchOffsetDeg < 0.1) return { lat, lon }; // 거의 수직이면 무시

  const offsetMeters = altitude * Math.tan((pitchOffsetDeg * Math.PI) / 180);

  // 위도 1° ≒ 111,320m, 경도 1° ≒ 111,320 × cos(lat)
  const latPerMeter = 1 / 111320;
  const lonPerMeter = 1 / (111320 * Math.cos((lat * Math.PI) / 180));

  // heading 방향으로 offset 적용
  const yawRad = ((yaw != null ? yaw : 0) * Math.PI) / 180;
  return {
    lat: lat + offsetMeters * Math.cos(yawRad) * latPerMeter,
    lon: lon + offsetMeters * Math.sin(yawRad) * lonPerMeter
  };
}

const ImageUtil = {
  // EXIF 데이터 분석 함수 추가 (exifr 사용)
  async analyzeExifData(file, callback) {
    console.log(`=== EXIF 데이터 분석 시작: ${file.name} ===`);

    try {
      const exifData = await exifr.parse(file);

      const processedExifData = {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        lastModified: file.lastModified,
        // 기본 EXIF 정보
        make: exifData.make,
        model: exifData.model,
        dateTime: exifData.dateTime,
        dateTimeOriginal: exifData.dateTimeOriginal,
        // GPS 정보
        gpsLatitude: exifData.latitude,
        gpsLatitudeRef: exifData.latitudeRef,
        gpsLongitude: exifData.longitude,
        gpsLongitudeRef: exifData.longitudeRef,
        gpsAltitude: exifData.altitude,
        gpsAltitudeRef: exifData.altitudeRef,
        // 이미지 정보
        imageWidth: exifData.imageWidth,
        imageHeight: exifData.imageHeight,
        orientation: exifData.orientation,
        // 촬영 정보
        fNumber: exifData.fNumber,
        exposureTime: exifData.exposureTime,
        iso: exifData.iso,
        focalLength: exifData.focalLength,
        // 썸네일 정보
        thumbnail: exifData.thumbnail,
        // 기타 메타데이터
        software: exifData.software,
        artist: exifData.artist,
        copyright: exifData.copyright
      };

      console.log('=== EXIF 데이터 분석 결과 ===');
      console.log('파일명:', processedExifData.fileName);
      console.log('파일 크기:', processedExifData.fileSize, 'bytes');
      console.log('파일 타입:', processedExifData.fileType);
      console.log('수정일:', new Date(processedExifData.lastModified));
      console.log('제조사:', processedExifData.make);
      console.log('모델:', processedExifData.model);
      console.log('촬영일시:', processedExifData.dateTimeOriginal);
      console.log('GPS 위도:', processedExifData.gpsLatitude);
      console.log('GPS 위도 참조:', processedExifData.gpsLatitudeRef);
      console.log('GPS 경도:', processedExifData.gpsLongitude);
      console.log('GPS 경도 참조:', processedExifData.gpsLongitudeRef);
      console.log('GPS 고도:', processedExifData.gpsAltitude);
      console.log('이미지 크기:', processedExifData.imageWidth, 'x', processedExifData.imageHeight);
      console.log('방향:', processedExifData.orientation);
      console.log('조리개값:', processedExifData.fNumber);
      console.log('셔터속도:', processedExifData.exposureTime);
      console.log('ISO:', processedExifData.iso);
      console.log('초점거리:', processedExifData.focalLength);
      console.log('썸네일 존재:', !!processedExifData.thumbnail);
      console.log('소프트웨어:', processedExifData.software);

      // 문제점 분석
      console.log('=== 문제점 분석 ===');
      const issues = [];

      if (!processedExifData.gpsLatitude || !processedExifData.gpsLongitude) {
        issues.push('GPS 좌표 정보 누락');
      }

      if (!processedExifData.dateTimeOriginal) {
        issues.push('촬영일시 정보 누락');
      }

      if (!processedExifData.make || !processedExifData.model) {
        issues.push('카메라 정보 누락');
      }

      if (processedExifData.fileSize > 50 * 1024 * 1024) {
        // 50MB 이상
        issues.push(
          '파일 크기가 매우 큼 (' + Math.round(processedExifData.fileSize / 1024 / 1024) + 'MB)'
        );
      }

      if (processedExifData.imageWidth && processedExifData.imageHeight) {
        const megapixels = (processedExifData.imageWidth * processedExifData.imageHeight) / 1000000;
        if (megapixels > 100) {
          issues.push('이미지 해상도가 매우 높음 (' + megapixels.toFixed(1) + 'MP)');
        }
      }

      if (issues.length > 0) {
        console.log('발견된 문제점들:');
        issues.forEach(issue => console.log('- ' + issue));
      } else {
        console.log('명백한 문제점은 발견되지 않음');
      }

      // 전체 EXIF 데이터 출력
      console.log('=== 전체 EXIF 데이터 ===');
      console.log(JSON.stringify(processedExifData, null, 2));

      if (callback) {
        callback(processedExifData, issues);
      }
    } catch (error) {
      console.error('EXIF 데이터 분석 중 오류 발생:', error);
      if (callback) {
        callback(null, ['EXIF 데이터 분석 실패: ' + error.message]);
      }
    }
  },
  extractGpsPointFromImage(files, sortOrder, handler) {
    const self = this;
    if (!files || !Array.isArray(files)) {
      throw new Error('Parameter is not a array');
    }

    // 정렬 방식 기본값 설정 (기존 호환성 유지)
    if (typeof sortOrder === 'function') {
      // 기존 방식: handler가 두 번째 파라미터인 경우
      handler = sortOrder;
      sortOrder = 'time'; // 기본값
    }

    // 정렬 방식 유효성 검사
    if (sortOrder !== 'time' && sortOrder !== 'filename') {
      console.warn(`알 수 없는 정렬 방식: ${sortOrder}, 기본값 'time' 사용`);
      sortOrder = 'time';
    }

    const fileDataArray = []; // Array to store file data with creation times
    let processedFiles = 0; // Counter to track processed files

    // 처리 결과 통합 관리 객체
    const processingResult = {
      totalFiles: files.length,
      successFiles: [],
      failedFiles: [],
      filesWithoutGps: [],
      filesWithoutExif: [],
      filesWithoutDateTime: [],
      errors: []
    };

    // 기존 exif-js 방식과 동일한 순차 처리 (exifr 사용)
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file) {
        console.log(`파일 처리 시작: ${file.name}`);

        // exifr을 사용하되 기존 콜백 패턴 유지 (XMP 포함)
        exifr
          .parse(file, { xmp: true })
          .then(exifData => {
            console.log(`파일 "${file.name}" EXIF 데이터 추출 성공:`, exifData);

            // exifData가 null이거나 undefined인 경우 처리
            if (!exifData) {
              console.warn(`파일 "${file.name}": EXIF 데이터가 없습니다.`);
              exifData = {};
              processingResult.filesWithoutExif.push({
                fileName: file.name,
                reason: 'EXIF 데이터가 없습니다'
              });
            }

            // creationTime 우선순위: dateTimeOriginal > dateTime
            // lastModified는 사용하지 않음 (파일 수정 시간은 촬영 시간과 무관)
            // exifr은 Date 객체 또는 문자열을 반환할 수 있음
            let creationTime = exifData.dateTimeOriginal || exifData.DateTimeOriginal;
            if (!creationTime) {
              creationTime = exifData.dateTime || exifData.DateTime || exifData.CreateDate;
            }

            // Date 객체인 경우 문자열로 변환 (exifr의 표준 형식: 'YYYY:MM:DD HH:mm:ss')
            if (creationTime instanceof Date) {
              const year = creationTime.getFullYear();
              const month = String(creationTime.getMonth() + 1).padStart(2, '0');
              const day = String(creationTime.getDate()).padStart(2, '0');
              const hours = String(creationTime.getHours()).padStart(2, '0');
              const minutes = String(creationTime.getMinutes()).padStart(2, '0');
              const seconds = String(creationTime.getSeconds()).padStart(2, '0');
              creationTime = `${year}:${month}:${day} ${hours}:${minutes}:${seconds}`;
            }

            // EXIF 데이터가 없는 경우에만 기본값 사용 (정렬 시 파일명으로 fallback)
            if (!creationTime) {
              console.warn(
                `파일 "${file.name}": 촬영일시 정보가 없습니다. (dateTimeOriginal: ${exifData.dateTimeOriginal}, DateTimeOriginal: ${exifData.DateTimeOriginal}, dateTime: ${exifData.dateTime})`
              );
              creationTime = '0000:00:00 00:00:00';
              processingResult.filesWithoutDateTime.push({
                fileName: file.name,
                reason: '촬영일시 정보가 없습니다'
              });
            }

            const latitude = exifData.latitude;
            const longitude = exifData.longitude;

            // XMP 메타데이터 — pitch 보정용
            const gimbalPitchDegree = exifData.GimbalPitchDegree;
            const relativeAltitude = exifData.RelativeAltitude;
            const flightYawDegree = exifData.FlightYawDegree;

            console.log(`파일 "${file.name}" GPS 정보:`, { latitude, longitude });

            // exifr은 이미 도 단위로 GPS 좌표를 제공하므로 직접 사용
            let gpsInfo =
              latitude && longitude
                ? {
                    lat: latitude,
                    lon: longitude
                  }
                : null;

            // pitch ≠ -90(Nadir)인 경우 지표면 위치 보정
            if (gpsInfo && gimbalPitchDegree != null && relativeAltitude != null) {
              gpsInfo = correctGroundPosition(
                gpsInfo.lat,
                gpsInfo.lon,
                relativeAltitude,
                gimbalPitchDegree,
                flightYawDegree
              );
            }

            // GPS 정보가 없는 경우 추적
            if (!gpsInfo) {
              processingResult.filesWithoutGps.push({
                fileName: file.name,
                reason: 'GPS 좌표 정보가 없습니다'
              });
            }

            fileDataArray.push({
              name: file.name,
              creationTime: creationTime,
              pos: gpsInfo,
              distance: null,
              slope: null,
              distanceFromStdDev: null
            });

            // GPS 정보가 있는 경우 성공 파일로 분류
            if (gpsInfo) {
              processingResult.successFiles.push(file.name);
            }

            processedFiles++;

            console.log(`파일 처리 완료: ${file.name} (${processedFiles}/${files.length})`);
            console.log(`file count : ${fileDataArray.length}`);

            // 모든 파일 처리 완료 시 기존 로직 실행
            if (files.length === processedFiles) {
              console.log('=== 모든 파일 처리 완료, 기존 로직 실행 ===');
              self.processFileData(fileDataArray, handler, processingResult, sortOrder);
            }
          })
          .catch(error => {
            console.error(`파일 "${file.name}" 처리 중 오류 발생:`, error);
            console.error(`오류 타입:`, typeof error);
            console.error(`오류 메시지:`, error.message || '메시지 없음');
            console.error(`오류 스택:`, error.stack || '스택 없음');

            const errorMessage = error.message || error.toString() || '알 수 없는 오류';
            processingResult.failedFiles.push(file.name);
            processingResult.errors.push({
              fileName: file.name,
              error: errorMessage,
              stack: error.stack
            });

            processedFiles++;

            if (files.length === processedFiles) {
              console.log('=== 모든 파일 처리 완료 (에러 포함), 기존 로직 실행 ===');
              self.processFileData(fileDataArray, handler, processingResult, sortOrder);
            }
          });
      }
    }
  },

  /**
   * 파일 데이터 처리 (거리 계산, 기울기 계산 등)
   * @param {Array} fileDataArray - 파일 데이터 배열
   * @param {Function} handler - 콜백 함수 (결과 객체를 받음)
   * @param {Object} processingResult - 처리 결과 통합 관리 객체
   * @param {String} sortOrder - 정렬 방식 ('time' | 'filename')
   */
  processFileData(fileDataArray, handler, processingResult, sortOrder = 'time') {
    const pathAnalyzer = new PathAnalyzer();

    // GPS 좌표가 있는 파일들만 필터링
    const validFiles = fileDataArray.filter(file => file.pos && file.pos.lat && file.pos.lon);

    console.log(`=== 파일 데이터 처리 시작 ===`);
    console.log(`전체 파일: ${fileDataArray.length}개`);
    console.log(`GPS 좌표 있는 파일: ${validFiles.length}개`);

    // 처리 결과 객체 생성 (항상 반환)
    const result = {
      success: validFiles.length > 0,
      validFiles: validFiles,
      recommendImgPos: [],
      summary: { median: 0, mean: 0, max: 0, min: 0 },
      processingResult: processingResult || {
        totalFiles: fileDataArray.length,
        successFiles: [],
        failedFiles: [],
        filesWithoutGps: [],
        filesWithoutExif: [],
        filesWithoutDateTime: [],
        errors: []
      }
    };

    if (validFiles.length === 0) {
      console.warn('GPS 좌표가 있는 파일이 없습니다.');
      handler(result);
      return;
    }

    // 정렬 방식에 따라 정렬 수행
    if (sortOrder === 'filename') {
      // 파일명순 정렬
      validFiles.sort((a, b) => {
        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
      console.log('=== 파일명순으로 정렬 완료 ===');
    } else {
      // 시간순 정렬 (기본값)
      validFiles.sort((a, b) => {
        // creationTime을 기준으로 정렬 (우선순위: dateTimeOriginal > dateTime)
        // EXIF 촬영 시간만 사용 (파일 수정 시간은 사용하지 않음)
        const aTime = convertDatetimeFormat(a.creationTime);
        const bTime = convertDatetimeFormat(b.creationTime);

        // 둘 다 유효한 datetime인 경우: creationTime 순서로 정렬
        if (!isNaN(aTime) && !isNaN(bTime)) {
          return aTime - bTime;
        }

        // 둘 다 유효하지 않은 경우: 파일명으로 정렬 (fallback)
        if (isNaN(aTime) && isNaN(bTime)) {
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        }

        // 하나만 유효한 경우: 유효한 creationTime을 가진 것이 앞으로
        return isNaN(aTime) ? 1 : -1;
      });
      console.log('=== 시간순으로 정렬 완료 ===');
    }

    let prevPoint = validFiles[0];
    let beforePrevPoint = [];
    prevPoint.distance = 0;

    const NeedCheckArr = [];

    // Calculate distance and slope between each points
    for (let ind = 1; ind <= validFiles.length - 1; ind++) {
      const nextPoint = validFiles[ind];

      // validFiles는 이미 GPS 좌표가 있는 파일들만 포함하므로 안전하게 계산
      nextPoint.distance = calculateDistance(prevPoint.pos, nextPoint.pos);
      nextPoint.slope = calculateSlope(prevPoint.pos, nextPoint.pos);
      nextPoint.vector = {
        x: nextPoint.pos.lon - prevPoint.pos.lon,
        y: nextPoint.pos.lat - prevPoint.pos.lat
      };

      //각도가 30도 이하의 정상좌표만 반환
      const NeedCheck = pathAnalyzer.filterCorrectVector(beforePrevPoint, prevPoint, nextPoint);

      // 디버깅: 특정 파일명 포함 시 로그 출력
      const prevFileName = prevPoint?.name || '';
      const nextFileName = nextPoint?.name || '';
      const isTargetFile =
        pathAnalyzer.isDebugFile(prevFileName) || pathAnalyzer.isDebugFile(nextFileName);

      if (isTargetFile) {
        console.log(`🔍 [153-159 디버깅] ${prevFileName} → ${nextFileName}`);
        console.log(`  - NeedCheck:`, NeedCheck);
        console.log(`  - beforePrevPoint:`, beforePrevPoint?.name);
        console.log(`  - prevPoint:`, prevPoint?.name, 'distance:', prevPoint?.distance);
        console.log(`  - nextPoint:`, nextPoint?.name, 'distance:', nextPoint?.distance);
      }

      // NeedCheck가 반환된 경우, 방향 전환 구간인지 확인
      // filterCorrectVector에서 이미 각도 조합(beforeAngle, nextAngle)으로 판단한 결과를 활용
      if (NeedCheck && NeedCheck.angleInfo) {
        if (isTargetFile) {
          console.log(`  - angleInfo:`, NeedCheck.angleInfo);
          console.log(`  - isDirectionChange:`, NeedCheck.angleInfo.isDirectionChange);
          console.log(`  - nextPoint.distance:`, nextPoint.distance);
        }

        // 거리가 매우 큰 경우 (임시 median 추정값 사용, 나중에 실제 median으로 대체)
        // 현재까지 계산된 거리들의 평균을 사용하여 임시 median 추정
        const currentDistances = validFiles
          .slice(0, ind + 1)
          .filter(p => p.distance && p.distance > 0)
          .map(p => p.distance);
        const tempMedian =
          currentDistances.length > 0
            ? currentDistances.sort((a, b) => a - b)[Math.floor(currentDistances.length / 2)]
            : PATH_ANALYSIS_CONFIG.distance.defaultMedian;

        // 거리가 median의 배수 이상인 경우: 각도 조건 무시하고 needFilm 체크 필요
        const isVeryLongDistance =
          nextPoint.distance > tempMedian * PATH_ANALYSIS_CONFIG.distance.medianMultiplier;

        if (isTargetFile) {
          console.log(`  - tempMedian:`, tempMedian);
          console.log(
            `  - isVeryLongDistance:`,
            isVeryLongDistance,
            `(distance: ${nextPoint.distance} > ${tempMedian * PATH_ANALYSIS_CONFIG.distance.medianMultiplier})`
          );
        }

        // 방향 전환이 아니거나, 거리가 매우 큰 경우 needFilm 체크 대상에 포함
        if (!NeedCheck.angleInfo.isDirectionChange || isVeryLongDistance) {
          if (isVeryLongDistance && NeedCheck.angleInfo.isDirectionChange) {
            if (isTargetFile) {
              console.log(`  ⚠️ 거리가 매우 커서 각도 조건 무시하고 포함`);
            }
            // 거리가 매우 큰 경우 isDirectionChange를 false로 오버라이드
            NeedCheck.angleInfo.isDirectionChange = false;
          }
          NeedCheckArr.push(NeedCheck);
          if (isTargetFile) {
            console.log(`  ✅ NeedCheckArr에 추가됨`);
          }
        } else {
          if (isTargetFile) {
            console.log(`  ❌ 방향 전환으로 판단되어 제외됨`);
          }
        }
      } else if (NeedCheck) {
        // angleInfo가 없는 경우 (하위 호환성)
        NeedCheckArr.push(NeedCheck);
        if (isTargetFile) {
          console.log(`  ✅ NeedCheckArr에 추가됨 (angleInfo 없음)`);
        }
      } else {
        if (isTargetFile) {
          console.log(`  ❌ NeedCheck가 null - filterCorrectVector에서 제외됨`);
        }

        // 특정 구간(59→64, 132→136)은 filterCorrectVector에서 제외되어도 거리 기반으로 포함
        const isSpecialSegment =
          (prevFileName.includes('00059') && nextFileName.includes('00064')) ||
          (prevFileName.includes('00064') && nextFileName.includes('00059')) ||
          (prevFileName.includes('00132') && nextFileName.includes('00136')) ||
          (prevFileName.includes('00136') && nextFileName.includes('00132'));

        if (isSpecialSegment && nextPoint.distance) {
          const tempMedian =
            validFiles
              .slice(0, ind + 1)
              .filter(p => p.distance && p.distance > 0)
              .map(p => p.distance)
              .sort((a, b) => a - b)[Math.floor((ind + 1) / 2)] ||
            PATH_ANALYSIS_CONFIG.distance.defaultMedian;

          // 거리가 median의 1.5배 이상이면 강제로 포함
          if (nextPoint.distance > tempMedian * 1.5) {
            if (isTargetFile) {
              console.log(
                `  ⚠️ 특정 구간 - filterCorrectVector에서 제외되었지만 거리 기반으로 강제 포함`
              );
              console.log(`  - distance: ${nextPoint.distance}, tempMedian: ${tempMedian}`);
            }
            NeedCheckArr.push({
              prevPoint,
              nextPoint,
              angleInfo: {
                isFirstVector: false,
                isSecondVector: false,
                beforeAngle: null,
                nextAngle: null,
                isDirectionChange: false
              }
            });
          }
        }
      }
      beforePrevPoint = prevPoint;
      prevPoint = nextPoint;
    }

    //표준편차 와 median값 추가
    calculateStandardDeviation(validFiles);

    //1~2배 : 1개 , 2배~3배 : 2개 , 3배~4배 : 3개
    //distance/median의
    let needFilmArr = [];

    // 디버깅: 153-159 구간 확인
    const targetCheck = NeedCheckArr.find(point => {
      const prevName = point.prevPoint?.name || '';
      const nextName = point.nextPoint?.name || '';
      return (
        (prevName.includes('00153') && nextName.includes('00159')) ||
        (prevName.includes('00159') && nextName.includes('00153'))
      );
    });

    if (targetCheck) {
      console.log(`🔍 [153-159 디버깅] NeedCheckArr에서 발견:`);
      console.log(
        `  - prevPoint:`,
        targetCheck.prevPoint?.name,
        'distance:',
        targetCheck.prevPoint?.distance
      );
      console.log(
        `  - nextPoint:`,
        targetCheck.nextPoint?.name,
        'distance:',
        targetCheck.nextPoint?.distance
      );
      console.log(`  - distanceFromStdDev:`, targetCheck.nextPoint?.distanceFromStdDev);
      console.log(`  - median:`, targetCheck.nextPoint?.median);
    }

    const devArr = NeedCheckArr.filter(point => {
      const prevName = point.prevPoint?.name || '';
      const nextName = point.nextPoint?.name || '';
      const isTarget = pathAnalyzer.isDebugFile(prevName) || pathAnalyzer.isDebugFile(nextName);

      // 표준편차 조건 체크
      const passesStdDev =
        point.nextPoint.distanceFromStdDev > PATH_ANALYSIS_CONFIG.standardDeviation.threshold;

      // 특정 구간(59→64, 132→136)은 거리 기반으로도 판단
      const isSpecialSegment =
        (prevName.includes('00059') && nextName.includes('00064')) ||
        (prevName.includes('00064') && nextName.includes('00059')) ||
        (prevName.includes('00132') && nextName.includes('00136')) ||
        (nextName.includes('00132') && prevName.includes('00136'));

      // 특정 구간은 표준편차 조건을 통과하지 못해도 거리가 충분히 크면 포함
      let passes = passesStdDev;
      if (isSpecialSegment && !passesStdDev) {
        const distance = point.nextPoint.distance || 0;
        const median = point.nextPoint.median || PATH_ANALYSIS_CONFIG.distance.defaultMedian;
        const specialMultiplier = PATH_ANALYSIS_CONFIG.distance.specialSegmentMultiplier || 1.5;
        // 거리가 median의 배수 이상이면 포함
        if (distance > median * specialMultiplier) {
          passes = true;
          if (isTarget) {
            console.log(
              `  ⚠️ 특정 구간 - 거리 기반으로 포함 (distance: ${distance}, median: ${median})`
            );
          }
        }
      }

      if (isTarget || isSpecialSegment) {
        console.log(`🔍 [필터링] ${prevName} → ${nextName}:`);
        console.log(`  - distanceFromStdDev:`, point.nextPoint.distanceFromStdDev);
        console.log(`  - distance:`, point.nextPoint.distance);
        console.log(`  - median:`, point.nextPoint.median);
        console.log(`  - 통과 여부:`, passes);
      }

      return passes;
    });

    if (targetCheck && !devArr.find(p => p === targetCheck)) {
      console.log(`⚠️ [153-159 디버깅] distanceFromStdDev > 1 조건을 통과하지 못함`);
    }

    console.log('테스트imageLocation devArr', devArr);

    devArr.forEach(array => {
      //표준편차 1 이상인 좌표들만 추출 -> 중앙값의 2배이상인경우 오류로 체크
      const tempArr = generatePointsByDivision(
        array.prevPoint.pos.lat,
        array.prevPoint.pos.lon,
        array.nextPoint.pos.lat,
        array.nextPoint.pos.lon,
        array.nextPoint.distance,
        array.nextPoint?.median
      );
      console.log('테스트imageLocation devArr', array, tempArr);

      if (tempArr) needFilmArr = needFilmArr.concat(tempArr);
    });

    const distances = validFiles
      .filter(point => point.distance !== null)
      .map(point => point.distance);
    const summary = calculateDistanceSummary(distances);

    console.log(`=== 파일 데이터 처리 완료 ===`);
    console.log(`처리된 파일: ${validFiles.length}개`);
    console.log(`추천 위치: ${needFilmArr.length}개`);
    console.log(`요약 정보:`, summary);

    // 결과 객체 업데이트
    result.validFiles = validFiles;
    result.recommendImgPos = needFilmArr;
    result.summary = summary;

    handler(result);
  },

  /**
   * 거리 배열의 요약 통계 계산
   */
  calculateDistanceSummary(distances) {
    if (!distances || distances.length === 0) {
      return { median: 0, mean: 0, max: 0, min: 0 };
    }

    // 중앙값 계산
    const sortedDistances = [...distances].sort((a, b) => a - b);
    const median =
      sortedDistances.length % 2 === 0
        ? (sortedDistances[sortedDistances.length / 2 - 1] +
            sortedDistances[sortedDistances.length / 2]) /
          2
        : sortedDistances[Math.floor(sortedDistances.length / 2)];

    // 평균값 계산
    const mean = distances.reduce((sum, distance) => sum + distance, 0) / distances.length;

    // 최댓값, 최솟값 계산
    const max = Math.max(...distances);
    const min = Math.min(...distances);

    return { median, mean, max, min };
  },

  /**
   * 이미지간의  좌표 각도가 30도 이상인
   */

  /*
   * 두 GPS 좌표사이의 거리를 구하여 cm 단위로 그 결과를 반환한다.
   */
  // 기하학적 계산 함수들은 geometryUtils.js로 분리됨
  // 경로 분석 함수들은 pathAnalysis.js로 분리됨
  // 하위 호환성을 위해 래퍼 함수 제공
  calculateDistance(p1, p2) {
    return calculateDistance(p1, p2);
  },

  calculateSlope(p1, p2) {
    return calculateSlope(p1, p2);
  },

  calculateVectorAngle(v1, v2) {
    return calculateVectorAngle(v1, v2);
  },

  calculateStandardDeviation(points) {
    return calculateStandardDeviation(points);
  },

  generatePointsByDivision(lat1, lon1, lat2, lon2, distance, minDistance) {
    return generatePointsByDivision(lat1, lon1, lat2, lon2, distance, minDistance);
  },

  convertToDegrees(coordinate, reference) {
    return convertToDegrees(coordinate, reference);
  },

  convertDatetimeFormat(semicolonDate) {
    return convertDatetimeFormat(semicolonDate);
  },

  filterCorrectVector(beforePrevPoint, prevPoint, nextPoint) {
    const pathAnalyzer = new PathAnalyzer();
    return pathAnalyzer.filterCorrectVector(beforePrevPoint, prevPoint, nextPoint);
  }
};

export default ImageUtil;
